import timerange from "abusesa-js/formats/timerange";
import timestamp from "abusesa-js/formats/timestamp";

const QUARTER_RE = /^\d{4}-Q\d$/i;
const YEAR_RE = /^\d{4}$/i;

const customStamps = {
    //normalise YYYY -> YYYY-01
    year: {
        test: str => str.length == 4 && YEAR_RE.test(str),
        start: str => str + "-1",
        end: str => str + "-12"
    },
    //normalise YYYY-QQ -> YYYY-MM
    quarter: {
        test: str => str.length == 7 && QUARTER_RE.test(str),
        start: str => str.slice(0, 5) + ((Number(str[6]) - 1) * 3 + 1),
        end: str => str.slice(0, 5) + (Number(str[6]) * 3)
    }
};

const parse = function(str, {yearAsTime}) {
    if (str instanceof Date) {
        return str;
    } else if (typeof str !== "string") {
        return null;
    }

    if (yearAsTime && customStamps.year.test(str)) {
        str = customStamps.year.start(str);
    } else if (customStamps.quarter.test(str)) {
        str = customStamps.quarter.start(str);
    }

    let span = timestamp.parse(str);

    return span? new Date(span) : null;
};

const isTime = function(string, opts) {
    return string instanceof Date || parse(string, opts) !== null;
};

const timeToSpan = function(str, {yearAsTime}) {
    if (typeof str !== "string") {
        return null;
    }

    if (yearAsTime && customStamps.year.test(str)) {
        str = customStamps.year.start(str) + " -- " + customStamps.year.end(str);
    } else if (customStamps.quarter.test(str)) {
        str = customStamps.quarter.start(str) + " -- " + customStamps.quarter.end(str);
    }

    return timerange.parse(str);
};

const pad = function(str, len) {
    str = String(str);
    while (str.length < len) {
        str = "0" + str;
    }
    return str;
};

// Calculate ISO 8601 week number and corresponding year for a date.
// The spec defines that:
// 1. A week starts from Monday
// 2. The 1st week of the year is the one that contains the first
//  Thurday on it (Thursday = UTCDay 4 or ISODay 3). Thus, Jan 1/2/3
//  can also happen to be on week 52 or 53 of the previous year, and
//  respectively Dec 29/30/31 can happend to be on the first week of next year.
const getISOWeek = date => {
    const year = date.getUTCFullYear();
    const DAY = 24 * 60 * 60 * 1000;

    let firstDay = (new Date(Date.UTC(year, 0, 1)).getUTCDay() + 6) % 7;
    let firstWeekOffset = firstDay > 3 ? 0 : 1;
    let firstMondayOffset = (7 - firstDay) % 7;
    let firstMonday = Date.UTC(year, 0, firstMondayOffset + 1);
    let ISOday = (date.getUTCDay() + 6) % 7;
    let thisWeeksMonday = Date.UTC(year, date.getUTCMonth(), date.getUTCDate()) - ISOday * DAY;

    let week = Math.round((thisWeeksMonday - firstMonday) / (7 * DAY)) + firstWeekOffset + 1;

    if (thisWeeksMonday < firstMonday && firstDay > 3) {
        return getISOWeek(new Date(Date.UTC(year, 0, 0)));
    } else if (new Date(thisWeeksMonday + DAY * 3).getUTCFullYear() !== year) {
        return getISOWeek(new Date(thisWeeksMonday + DAY * 3));
    }

    return {year, week};
};

const symbols = {
    "Y": {
        len: 4,
        get: function(date) {
            return date.getUTCFullYear();
        },
        set: function(date, val) {
            date.setUTCFullYear(val);
            return date;
        }
    },
    "m": {
        first: 1,
        len: 2,
        get: function(date) {
            return date.getUTCMonth() + 1;
        },
        set: function(date, val) {
            date.setUTCMonth(val - 1);
            return date;
        }
    },
    "Q": {
        first: 1,
        len: 1,
        get: function(date) {
            return Math.ceil((date.getUTCMonth() + 1) / 3);
        },
        set: function(date, val) {
            date.setUTCMonth(((val - 1) * 3));
            return date;
        },
        normalize: function(from) {
            const match = from.match(/Q[1-4]/);
            if (match) {
                const q = Number(match[0][1]);
                from = from.replace(match[0], pad((q - 1) * 3 + 1, 2));
            }
            return from;
        }
    },
    "d": {
        first: 1,
        len: 2,
        get: function(date) {
            return date.getUTCDate();
        },
        set: function(date, val) {
            date.setUTCDate(val);
            return date;
        }
    },

    "G": {
        len: 4,
        get: function(date) {
            return getISOWeek(date).year;
        },
        set: function(...args) {
            return symbols.Y.set(...args);
        }
    },

    "W": {
        first: 1,
        len: 2,
        get: function(date) {
            return getISOWeek(date).week;
        },
        set: function(date, val) {
            const year = date.getUTCFullYear();
            const DAY = 24 * 60 * 60 * 1000;

            let firstDay = (new Date(Date.UTC(year, 0, 1)).getUTCDay() + 6) % 7;
            let firstWeekOffset = firstDay > 3 ? 1 : 2;
            let firstMondayOffset = (7 - firstDay) % 7;
            let firstMonday = Date.UTC(year, 0, 1 + firstMondayOffset);

            return new Date(firstMonday + (val - firstWeekOffset) * DAY * 7);
        }
    },
    "H": {
        len: 2,
        get: function(date) {
            return date.getUTCHours();
        },
        set: function(date, val) {
            date.setUTCHours(val);
            return date;
        }
    },
    "M": {
        len: 2,
        get: function(date) {
            return date.getUTCMinutes();
        },
        set: function(date, val) {
            date.setUTCHours(val);
            return date;
        }
    }
};

const _get = function(date, s, round) {
    const _s = symbols[s];
    let val = _s.get(date);

    round = Number(round);
    if (round) {
        // when the index doesn't start from 0 (days, months..)
        // shift the value accordingly before and after rounding
        const first = _s.first || 0;
        val = Math.floor((val - first) / round) * round + first;
    }

    return pad(val, _s.len);
};

const _set = function(date, s, value) {
    const _s = symbols[s];
    return _s.set(new Date(date), Number(value));

};

const _normalize = function(string) {
    let result = string;
    Object.keys(symbols).forEach(function(s) {
        if (symbols[s].normalize) {
            result = symbols[s].normalize(result);
        }
    });

    return result;
};

const format = function(timeformat) {
    return function(d) {
        let ts;
        if (d instanceof Date) {
            ts = d.getTime();
        } else {
            ts = looseParseTime(d);
            if (!ts) {
                return null;
            }
        }

        const date = new Date(ts);
        const re = /(?:%([A-z]))(?:\/(\d+))?/g;
        let res = timeformat;
        let match;

        while ((match = re.exec(timeformat)) !== null) {
            res = res.replace(match[0], _get(date, match[1], match[2]));
        }

        return res;
    };
};


const multiFormatter = _formats => {
    let formats = _formats.map(([timeformat, test]) => [format(timeformat), test]);

    return stamp => {
        for (let [formatter, test] of formats) {
            if (test(stamp)) {
                return formatter(stamp);
            }
        }
    };
    
};

const HOURS_REGEX = /^\d{4}-\d\d?-\d\d?[T ]\d\d?/;

// Timezone offset from utc in minutes, as given by time.getTimezoneOffset().
// Apply timezone offsetonly when timestamp format precision is at least hours
const roundTo = function(timeformat, timezoneOffset = 0) {
    timezoneOffset *= 60 * 1000; //convert to ms for easier usage

    return function(from) {
        let ts;

        if (typeof from === "string") {
            from = _normalize(from);
        }

        let offset = timezoneOffset;

        if (from instanceof Date) {
            ts = from.getTime();
        } else {
            ts = looseParseTime(from);
            if (!ts) {
                return null;
            }

            offset = HOURS_REGEX.test("" + from) ? timezoneOffset : 0;
        }

        let result = new Date(Date.UTC(0, 0));
        const re = /(?:%([A-Za-z]))(?:\/(\d+))?/g;
        let match;

        while ((match = re.exec(timeformat)) !== null) {
            const symbol = match[1];

            result = _set(result, symbol, _get(new Date(ts - offset), symbol, match[2]));
        }

        return result;
    };
};

const increase = {
    "second": (date, seconds) => {
        let d = new Date(date);
        d.setUTCSeconds(d.getUTCSeconds() + seconds);
        return d;
    },
    "minute": (date, minutes) => {
        let d = new Date(date);
        d.setUTCMinutes(d.getUTCMinutes() + minutes);
        return d;
    },
    "hour": (date, hours) => {
        let d = new Date(date);
        d.setUTCHours(d.getUTCHours() + hours);
        return d;
    },
    "day": (date, days) => {
        let d = new Date(date);
        d.setUTCDate(d.getUTCDate() + days);
        return d;
    },
    "week": (date, weeks) => {
        return increase.day(date, weeks * 7);
    },
    "month": (date, months) => {
        let d = new Date(date);
        d.setUTCMonth(d.getUTCMonth() + months);
        return d;
    },
    "quarter": (date, quarters) => {
        return increase.month(date, quarters * 3);
    },
    "year": (date, years) => {
        let d = new Date(date);
        d.setUTCFullYear(d.getUTCFullYear() + years);
        return d;
    }
};

const timeformats = {
    "minute": "%Y-%m-%d %H:%M",
    "hour": "%Y-%m-%d %H",
    "day": "%Y-%m-%d",
    "week": "%G-W%W",
    "month": "%Y-%m",
    "quarter": "%Y-Q%Q",
    "year": "%Y"
};


const looseParseTime = str => parse(str, {yearAsTime: true});
const strictParseTime = str => parse(str, {yearAsTime: false});
const looseIsTime = str => isTime(str, {yearAsTime: true});
const strictIsTime = str => isTime(str, {yearAsTime: false});
const looseTimeToSpan = str => timeToSpan(str, {yearAsTime: true});

export {
    strictIsTime,
    looseIsTime,
    strictIsTime as isTime,

    looseParseTime,
    strictParseTime,
    looseParseTime as parse,

    looseTimeToSpan as timeToSpan,

    format,
    multiFormatter,

    roundTo as round,
    timeformats,
    increase
};



// WEBPACK FOOTER //
// ./src/kpi/lib/TimeUtils.js